import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useWpCartService } from '@cart-services/hooks/useWpCartService'
import { type PartialCartItem } from '@cart-services/types'
import { type CountryCode, DeliveryClass } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { wordPressNavigate } from '@utils'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { DIALOG_ADD_TO_CART_ERROR } from './DialogAddToCartError'

export const DIALOG_BUY_AGAIN = 'DIALOG_BUY_AGAIN'
export interface DialogBuyAgainProps {
  environment: 'wordPress' | 'merchant' | 'campaign'
  countryCode: CountryCode
  campaignId?: number
  items: PartialCartItem[]
}

const useShowSnackbar = ({ handleGoToCart }: { handleGoToCart: () => void }) => {
  const { t } = useTranslation()
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  return () => {
    openSnackbar({
      message: t('wpOrder.addedSuccessfully', { defaultValue: '訂單商品已加入購物車！' }),
      severity: 'success',
      action: (
        <Button
          variant="text"
          color="blue"
          onClick={handleGoToCart}
        >
          {t('wpOrder.checkout', { defaultValue: '前往結帳' })}
        </Button>
      ),
    })
  }
}

const DialogBuyAgainBody = ({ handleBuyThisOrderOnly, handleAddToCart, onClose }: {
  handleBuyThisOrderOnly: () => void
  handleAddToCart: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()

  return (
    <>
      <DialogHeader align="center" title={t('wpOrder.repeatOrder', { defaultValue: '再買一次' })} onClose={onClose} />
      <DialogContent>
        <Text align="center" color={colors.grey800}>
          <Trans
            i18nKey="wpOrder.buyAgainText"
            components={{
              Br: <div />,
            }}
          />
        </Text>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleBuyThisOrderOnly}
          fullWidth
          variant="outlined"
          color="grey"
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.buyThisOrderOnly', { defaultValue: '僅加入此訂單' })}
        </Button>
        <Button
          onClick={handleAddToCart}
          fullWidth
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.addToCart', { defaultValue: '加入購物車' })}
        </Button>
      </DialogActions>
    </>
  )
}

const MerchantDialogBuyAgainComponent = () => {
  const { closeDialog, countryCode, campaignId, items, environment } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)
  const { upsertCartItem, overwriteCartItems, items: cartItems } = useFpB2bCartService(campaignId ?? null, Platform.MERCHANT)
  const { openDialog: openDialogAddToCartError } = useDialog(DIALOG_ADD_TO_CART_ERROR)

  const { campaignCodeFromUrl } = useCampaignCode()
  const isCampaignEnvironment = environment === 'campaign'
  const history = useHistory()
  const showSnackbar = useShowSnackbar({
    handleGoToCart: () => {
      if (isCampaignEnvironment) {
        history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
      } else {
        history.push(`/merchant/${campaignCodeFromUrl}/cart`)
      }
    },
  })

  const handleBuyThisOrderOnly = useCallbackEvent(() => {
    overwriteCartItems(items)
    showSnackbar()
    closeDialog()
  })

  const handleAddToCart = useCallbackEvent(() => {
    const hasFrozenDelivery = cartItems.some(item => item.delivery_class === DeliveryClass.FROZEN)
    const hasNormalDelivery = cartItems.some(item => item.delivery_class === DeliveryClass.NORMAL)
    const hasDeliveryClassConflict = items.some((item) => {
      const deliveryClass = item.delivery_class
      return (deliveryClass === DeliveryClass.FROZEN && hasNormalDelivery) || (deliveryClass === DeliveryClass.NORMAL && hasFrozenDelivery)
    })
    if (hasDeliveryClassConflict) {
      closeDialog()
      openDialogAddToCartError({ environment: isCampaignEnvironment ? 'campaign' : 'merchant', countryCode, campaignId, items })
      return
    }
    items.forEach(item => upsertCartItem(item))
    showSnackbar()
    closeDialog()
  })

  return (
    <DialogBuyAgainBody
      onClose={closeDialog}
      handleBuyThisOrderOnly={handleBuyThisOrderOnly}
      handleAddToCart={handleAddToCart}
    />
  )
}

const WpDialogBuyAgainComponent = () => {
  const { closeDialog, countryCode, items } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)
  const { overwriteCartItems, addCartItem, isCartItemAddForbidden } = useWpCartService(countryCode)
  const { openDialog: openDialogAddToCartError } = useDialog(DIALOG_ADD_TO_CART_ERROR)
  const showSnackbar = useShowSnackbar({ handleGoToCart: () => wordPressNavigate('/cart/') })

  const handleAddToCart = useCallbackEvent(() => {
    const hasForbiddenItem = items.some((item) => {
      const forbiddenResult = isCartItemAddForbidden({ itemWantToAdd: item })
      return forbiddenResult && (forbiddenResult.mixedDeliveryClassIsForbidden || forbiddenResult.addSubshipIsForbidden)
    })
    if (hasForbiddenItem) {
      closeDialog()
      openDialogAddToCartError({ environment: 'wordPress', countryCode, items })
      return
    }
    items.forEach(item => addCartItem(item))
    closeDialog()
    showSnackbar()
  })

  const handleBuyThisOrderOnly = useCallbackEvent(() => {
    overwriteCartItems(items)
    closeDialog()
    showSnackbar()
  })

  return (
    <DialogBuyAgainBody
      onClose={closeDialog}
      handleBuyThisOrderOnly={handleBuyThisOrderOnly}
      handleAddToCart={handleAddToCart}
    />
  )
}

export const DialogBuyAgain = () => {
  const { open, closeDialog, environment } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)

  return (
    <Dialog onClose={closeDialog} open={open} maxWidth={320} fullWidth>
      {open && (environment === 'merchant' || environment === 'campaign'
        ? <MerchantDialogBuyAgainComponent />
        : <WpDialogBuyAgainComponent />)}
    </Dialog>
  )
}
